import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_TITLE = "Nougat | Vienna Legal Operations Agent";
const DEFAULT_DESCRIPTION =
  "Vienna's legal operations workspaces support civil litigation, criminal defense, startups and VC, and in-house teams. Work across email, Slack, and Teams.";
const DEFAULT_IMAGE = "https://nougat.law/social-preview.png";

const PAGE_META = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/products": {
    title: "Vienna Legal Operations Workspaces | Nougat",
    description:
      "Hire Vienna for civil litigation, criminal defense, and startup & VC work: one legal operations agent for research, drafting, and end-to-end matter workflows.",
  },
  "/startups-vc": {
    title: "Nougat for Startups & VC | Vienna Legal Ops",
    description:
      "Vienna connects startup formation, fundraising, diligence, governance, and closing workflows for founders, investors, and startup counsel.",
  },
  "/criminal": {
    title: "Nougat for Criminal Defense | Vienna Legal Ops",
    description:
      "AI-powered criminal defense for attorneys handling federal or state investigations and criminal matters: motion and brief drafting, discovery analysis, research citations, and hallucination detection — now built into Vienna.",
  },
  "/civil": {
    title: "Nougat for Civil Litigation | Vienna Legal Ops",
    description:
      "AI-powered civil litigation for attorneys and self-represented litigants, from first demand through final judgment: docket monitoring, deadline tracking, motion drafting, and validated research citations — built into Vienna.",
  },
  "/cite-checker": {
    title: "Citation Checker | Nougat",
    description:
      "Verify legal citations before filing with Vienna's citation checker for briefs, motions, and memoranda.",
  },
  "/watch": {
    title: "Watch the Vienna Demo | Nougat",
    description:
      "Watch the Vienna product demo to see legal research, drafting, and matter workflow automation in action.",
  },
  "/pricing-cards": {
    title: "Pricing | Nougat",
    description:
      "Legal AI for litigators, law firms, legal teams, startups, & VCs: compare Vienna pricing plans for every stage of legal practice.",
  },
  "/small-firm": {
    title: "Small Firm Plan | Nougat",
    description:
      "Legal AI for litigators, law firms, legal teams, startups, & VCs: practical workflows for docket tracking, drafting, and case preparation.",
  },
  "/big-firm": {
    title: "Big Firm Plan | Nougat",
    description:
      "Legal AI for litigators, law firms, legal teams, startups, & VCs: scale legal intelligence across matters, documents, and team collaboration.",
  },
  "/blog": {
    title: "Blog | Nougat",
    description:
      "Legal AI for litigators, law firms, legal teams, startups, & VCs: read legal technology insights, practical litigation guidance, and product updates.",
  },
  "/product-documentation": {
    title: "Product Documentation | Nougat",
    description:
      "Public Vienna product documentation: getting started, core workflows, backend + email agent mental model, and support contacts.",
  },
  "/changelog": {
    title: "Changelog | Nougat",
    description:
      "Public Vienna changelog with manually curated product updates, release notes, and notable improvements.",
  },
  "/contact": {
    title: "Contact Sales | Nougat",
    description:
      "Legal AI for litigators, law firms, legal teams, startups, & VCs: talk with Vienna about solutions for your practice or organization.",
  },
  "/partnerships": {
    title: "Partnerships | Nougat",
    description:
      "Legal AI for litigators, law firms, legal teams, startups, & VCs: partner with Vienna to expand access to legal intelligence tools and workflows.",
  },
  "/book-a-demo": {
    title: "Book a Demo | Nougat",
    description:
      "Legal AI for litigators, law firms, legal teams, startups, & VCs: book a demo to see Vienna for research, drafting, and docket collaboration.",
  },
  "/terms-of-service": {
    title: "Terms of Service | Nougat",
    description: "Legal AI for litigators, law firms, legal teams, startups, & VCs: read the Vienna terms of service.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Nougat",
    description: "Legal AI for litigators, law firms, legal teams, startups, & VCs: read the Vienna privacy policy.",
  },
  "/law": {
    // Must match the <title> LawLanding.tsx sets via Helmet. Both write
    // document.title and the later one wins; this previously said
    // "Law Library | Nougat" while the page rendered "Free Online Law
    // Library", so which one a crawler saw depended on effect ordering.
    title: "Free Online Law Library",
    description:
      "Search statutes, rules, and case law in the Vienna law library, with citations you can verify before you file.",
  },
  "/compare": {
    title: "Compare Vienna | Nougat",
    description:
      "How Vienna, a legal operations AI agent that works from email, Slack, and Teams, compares with CoCounsel, Harvey, and Lexis+ AI.",
  },
  // Must match the title/description of each entry in src/lib/compare/comparisons.js.
  "/compare/vienna-vs-cocounsel": {
    title: "Vienna vs CoCounsel | Nougat",
    description:
      "How Vienna, a legal operations agent that works from email, Slack, and Teams, compares with Thomson Reuters CoCounsel for research, drafting, citations, and matter work.",
  },
  "/compare/vienna-vs-harvey": {
    title: "Vienna vs Harvey | Nougat",
    description:
      "How Vienna, a legal operations agent for litigators and smaller firms, compares with Harvey for research, drafting, citations, and day-to-day matter work.",
  },
  "/compare/vienna-vs-lexis-plus-ai": {
    title: "Vienna vs Lexis+ AI | Nougat",
    description:
      "How Vienna, a legal operations agent that works from email, Slack, and Teams, compares with LexisNexis Lexis+ AI for research, citations, drafting, and matter work.",
  },
  "/faq": {
    title: "FAQ | Nougat",
    description:
      "Answers to common questions about Vienna: how the Legal Operations Agent works, what it drafts, how citations are verified, and how your matter data is handled.",
  },
  "/private-attorney": {
    title: "Private Attorney Plan | Nougat",
    description:
      "Legal AI for solo and private-practice attorneys: docket tracking, drafting, citation verification, and client correspondence in one Legal Operations Agent.",
  },
  "/product-documentation/faq-structured-data": {
    // Must match the <title> FaqDocsPage.jsx sets via Helmet. Both systems write
    // document.title for this route and the later one wins, so a mismatch makes
    // the rendered title depend on effect ordering.
    title: "FAQ structured data | Nougat Docs",
    description:
      "Reference documentation for the FAQ structured data Vienna publishes for search engines.",
  },
  "/product-documentation/zoom-integration": {
    title: "Zoom Integration Guide | Nougat Docs",
    description:
      "Connect Zoom to Vienna scheduling, manage authorization, and troubleshoot meeting creation.",
  },
  "/pricing": {
    title: "Vienna Pricing Plans | Nougat",
    description:
      "Compare Vienna plans for solo attorneys, small firms, and large legal teams, and see what each tier includes.",
    canonical: "https://nougat.law/pricing-cards",
  },
  "/docs": {
    title: "Documentation | Nougat",
    description:
      "Product documentation for Vienna: how the Legal Operations Agent drafts, verifies citations, tracks dockets, and connects to your firm's tools.",
  },
  "/slack": {
    title: "Vienna for Slack | Nougat",
    description:
      "Install Vienna in Slack to draft filings, check citations, and track deadlines without leaving the channel your matter already lives in.",
  },
  "/zoom-integration": {
    title: "Vienna for Zoom | Nougat",
    description:
      "Connect Zoom to Vienna for automatic meeting prep, transcripts, and follow-up tasks tied to the right matter.",
  },
  "/iltacon-2026": {
    // Must match the <title> ILTACON.jsx sets via Helmet.
    title: "ILTACON 2026 | Case & Litigation Management That Runs From Your Inbox | Nougat",
    description:
      "Meet Vienna at ILTACON 2026: case and litigation management that runs from your inbox.",
  },
  "/login": {
    title: "Log In | Nougat",
    description: "Legal AI for litigators, law firms, legal teams, startups, & VCs: log in to your Vienna account.",
    robots: "noindex, nofollow",
  },
  "/signup": {
    title: "Sign Up | Nougat",
    description: "Legal AI for litigators, law firms, legal teams, startups, & VCs: create your Vienna account.",
    // Indexable on purpose: this is a conversion landing page people search for
    // by name, and it is advertised in sitemap.xml. /login and /forgot-password
    // are not -- they are account plumbing, not entry points.
  },
  "/forgot-password": {
    title: "Forgot Password | Nougat",
    description: "Legal AI for litigators, law firms, legal teams, startups, & VCs: reset your Vienna account password.",
    robots: "noindex, nofollow",
  },
};

// Authenticated and matter-scoped surfaces must never enter the index. Without
// this every unknown route fell through to "index, follow" plus a generic
// title, which floods Search Console with thin duplicate pages -- and would put
// anonymously-readable case digests under /dashboard/{appId}/cases/... in
// Google. robots.txt deliberately stays permissive (Disallow:) so crawlers can
// still reach the marketing pages; exclusion is expressed per-page here.
//
// Mirrors Atticus-Front-End/src/lib/seo/seoConfig.js `noIndexPrefixes`.
const NO_INDEX_PREFIXES = [
  "/dashboard",
  "/dev_test",
  "/schedule",
  "/dashboard+msft-auth-validated",
  "/dashboard+google-auth-validated",
  "/dashboard+yahoo-auth-validated",
  "/whatsapp/link",
  "/cases",
  "/caseflow",
  "/integrations",
  // The firm tools / connections page. Vienna and Vienna both noindex this;
  // juristai-home did not, so an authenticated integrations surface was the one
  // app route still advertising itself as indexable here.
  "/connections",
  "/inbox-review",
  "/invite",
  "/auth/callback",
  "/logout",
  "/password-reset",
  "/customer-support",
  "/zoom-test-plan",
  "/product-documentation/zoom-test-plan",
  // Retain the legacy pricing page without publishing it in public metadata.
  "/pro-se-plan",
];

const isNoIndexPath = (pathname) =>
  NO_INDEX_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

// Sections that legitimately generate indexable URLs we cannot enumerate in
// PAGE_META (law library documents, blog posts, docs pages). Anything else that
// is not in PAGE_META is an unrouted URL: the SPA answers it with the NotFound
// screen but nginx still returns HTTP 200, so Google would file it as a soft
// 404 or as a duplicate of the home page. Mark those noindex instead.
const INDEXABLE_DYNAMIC_PREFIXES = [
  "/law",
  "/blog",
  // Legacy /blog-post/<id> links redirect to their /blog/<slug> article; keep
  // them indexable so the redirect, not a noindex tag, is what crawlers see.
  "/blog-post",
  "/product-documentation",
];

// Routes whose <head> is rendered by react-helmet-async instead of by the
// imperative writes below. Two systems writing the same tag leave two of them.
const HELMET_OWNED_PREFIXES = ["/law", "/iltacon-2026"];

const isHelmetOwnedPath = (pathname) =>
  HELMET_OWNED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

const isIndexableDynamicPath = (pathname) =>
  INDEXABLE_DYNAMIC_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

const ensureMetaTag = (selector, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attrs).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }
  return tag;
};

const SEOHead = () => {
  const location = useLocation();

  useEffect(() => {
    const { pathname } = location;
    const normalizedPath = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
    const baseMeta = PAGE_META[normalizedPath] || {
      // Fall back to the real site title, not a second brand string: an unknown
      // route reporting a title no other page uses reads as a distinct page.
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      robots:
        isNoIndexPath(normalizedPath) || !isIndexableDynamicPath(normalizedPath)
          ? "noindex, nofollow"
          : undefined,
    };

    // Nougat's apex host is the canonical public origin.
    const canonicalUrl = baseMeta.canonical || `https://nougat.law${normalizedPath || "/"}`;

    // /law/* renders its head with react-helmet-async (LawLanding,
    // LawDocumentPage), which appends its OWN <link rel="canonical"> and
    // <meta name="description"> rather than reusing the ones written here.
    // Both running produced two of each on /law -- Google treats duplicate
    // canonicals as no canonical at all. Which tag "wins" depended purely on
    // effect ordering, so this looked fine when measured once.
    //
    // Helmet owns title/canonical/description/og on these routes. Its title
    // must remain authoritative; this parent effect runs after route children
    // and otherwise overwrites their document-specific title with the generic
    // PAGE_META fallback. SEOHead still owns robots on these routes.
    if (!isHelmetOwnedPath(normalizedPath)) {
      document.title = baseMeta.title;
    }

    if (isHelmetOwnedPath(normalizedPath)) {
      // Helmet owns the canonical on law pages. Remove any stale imperative or
      // static copy left by the previous route before Helmet publishes its one
      // data-rh canonical; two canonical tags make Google treat the signal as
      // ambiguous even when both hrefs happen to match.
      document.head
        .querySelectorAll("link[rel='canonical']:not([data-rh='true'])")
        .forEach((tag) => tag.remove());

      // index.html also ships a static description, and Helmet appends rather
      // than replacing it -- so standing back is not enough on its own. Drop
      // the copies Helmet does not manage (it stamps its own with data-rh).
      // Navigating away recreates them: ensureMetaTag creates when missing.
      // og:* and twitter:* are in here too. LawDocumentPage sets all of them,
      // and measuring showed 2 of each on a document page while canonical and
      // description were already down to 1 -- the first pass of this fix only
      // covered the two tags I had thought to check. Duplicate og tags mean a
      // shared link previews with whichever copy the platform happens to read.
      // index.html marks the tags Helmet also emits with data-rh, so Helmet
      // REPLACES them rather than appending a second copy. That is Vienna's
      // existing pattern and it is not timing-dependent -- the removal logic
      // that used to live here raced Helmet's first render and could strip a
      // tag Helmet had not published yet.
      const lawRobots = ensureMetaTag("meta[name='robots']", { name: "robots" });
      lawRobots.setAttribute(
        "content",
        isNoIndexPath(normalizedPath) ? "noindex, nofollow" : "index, follow"
      );
      return;
    }


    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const descriptionTag = ensureMetaTag("meta[name='description']", {
      name: "description",
    });
    descriptionTag.setAttribute("content", baseMeta.description);

    const robotsTag = ensureMetaTag("meta[name='robots']", {
      name: "robots",
    });
    robotsTag.setAttribute("content", baseMeta.robots || "index, follow");

    const ogTitle = ensureMetaTag("meta[property='og:title']", {
      property: "og:title",
    });
    ogTitle.setAttribute("content", baseMeta.title);

    const ogDescription = ensureMetaTag("meta[property='og:description']", {
      property: "og:description",
    });
    ogDescription.setAttribute("content", baseMeta.description);

    const ogUrl = ensureMetaTag("meta[property='og:url']", {
      property: "og:url",
    });
    ogUrl.setAttribute("content", canonicalUrl);

    const ogImage = ensureMetaTag("meta[property='og:image']", {
      property: "og:image",
    });
    ogImage.setAttribute("content", DEFAULT_IMAGE);

    const twitterTitle = ensureMetaTag("meta[name='twitter:title']", {
      name: "twitter:title",
    });
    twitterTitle.setAttribute("content", baseMeta.title);

    const twitterDescription = ensureMetaTag("meta[name='twitter:description']", {
      name: "twitter:description",
    });
    twitterDescription.setAttribute("content", baseMeta.description);

    const twitterImage = ensureMetaTag("meta[name='twitter:image']", {
      name: "twitter:image",
    });
    twitterImage.setAttribute("content", DEFAULT_IMAGE);
  }, [location]);

  return null;
};

export default SEOHead;
